import React, { useEffect, useState } from 'react';
import Button from 'components/crm/ui/Button';
import Modal from 'components/crm/ui/Modal';
import useText from 'hooks/useText';

export const SCOPES = ['this', 'following', 'all'];

// Mounted while open only: each opening starts on the first choice offered.
const ScopeChoice = ({ action, ruleChanged, onCancel, onConfirm }) => {
  const { text } = useText('appointmentScope');
  // A new rule cannot apply to one event alone, as in Google Calendar.
  const scopes = ruleChanged
    ? SCOPES.filter((scope) => scope !== 'this')
    : SCOPES;
  const [scope, setScope] = useState(scopes[0]);
  // Until the answer of `onConfirm` (a promise or nothing): one click only,
  // the button says what is happening.
  const [pending, setPending] = useState(false);
  const confirm = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onConfirm(scope);
    } finally {
      setPending(false);
    }
  };
  // The Modal closes on Échap; marked handled first, so that the form or the
  // slide-over around it stays open.
  useEffect(() => {
    const onKeyDown = (event) =>
      event.key === 'Escape' && event.preventDefault();
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <Modal
      open
      onClose={onCancel}
      title={text(action === 'delete' ? 'deleteTitle' : 'editTitle')}
      width={400}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            {text('cancel')}
          </Button>
          <Button onClick={confirm} disabled={pending}>
            {pending ? text(action === 'delete' ? 'deletePending' : 'editPending') : text('ok')}
          </Button>
        </>
      }
    >
      <fieldset className="flex flex-col gap-3 text-sm text-body">
        {scopes.map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="appointment-scope"
              value={value}
              checked={scope === value}
              onChange={() => setScope(value)}
            />
            {text(`scope_${value}`)}
          </label>
        ))}
      </fieldset>
    </Modal>
  );
};

/**
 * The choice of Google Calendar on a repeated event (`open`): « Cet
 * événement », « Cet événement et les suivants » or « Tous les événements »,
 * to modify it (`action` 'edit') or delete it ('delete'). When its rule
 * changed (`ruleChanged`), « Cet événement » is not offered. « OK » hands the
 * scope ('this' | 'following' | 'all') to `onConfirm`; « Annuler », Échap or
 * the backdrop call `onCancel`.
 */
const ScopeDialog = ({
  open,
  action = 'edit',
  ruleChanged = false,
  onCancel,
  onConfirm,
}) =>
  open ? (
    <ScopeChoice
      action={action}
      ruleChanged={ruleChanged}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  ) : null;

export default ScopeDialog;
